import type { CSSProperties } from 'react';
import { LOCALES } from '@/i18n';
import { FLAGS, useNavModel, type NavItem } from '@/components/layout/useNavModel';

/**
 * Material 3 (Android) skin of the desktop/tablet navigation chrome: a solid,
 * edge-flush navigation rail (see `.app-rail` in styles.css). Used on both
 * tablet and expanded windows under the default `md` base. Unlike the iOS glass
 * `Sidebar` it does NOT float over the content - it occupies real layout space
 * and the workspace sits beside it. The selected destination carries the M3
 * "active indicator": a pill behind the icon. Pure presentation over
 * `useNavModel()`, so it always stays in sync with the iOS sidebar.
 */
export default function NavigationRail() {
  const {
    t,
    email,
    locale,
    currentLang,
    langOpen,
    setLangOpen,
    langRef,
    operationsItems,
    functionalityItems,
    activeItemPath,
    navigate,
    handleLogout,
    selectLocale,
    getInitials,
  } = useNavModel();

  const renderItem = (item: NavItem) => {
    const active = !!item.path && item.path === activeItemPath;
    return (
      <button
        key={item.path ?? item.title}
        type="button"
        data-nav-path={item.path}
        className={`app-rail-item${active ? ' app-rail-item-active' : ''}`}
        onClick={() => (item.action ? item.action() : navigate(item.path!))}
        title={item.warning ? `${item.title}: ${item.warning}` : item.title}
      >
        <span className="app-rail-indicator">
          <i
            className="f7-icons app-rail-icon"
            style={{ '--nav-icon-color': item.color } as CSSProperties}
          >
            {item.icon}
          </i>
          {!!item.badge && <span className="app-rail-badge">{item.badge}</span>}
          {item.warning && (
            <i className="f7-icons app-rail-warning" aria-label={item.warning}>
              exclamationmark_triangle_fill
            </i>
          )}
        </span>
        <span className="app-rail-label">{item.title}</span>
      </button>
    );
  };

  return (
    <div className="app-rail">
      {/* Brand / menu anchor (M3 rails place the FAB or logo at the top) */}
      <div className="app-rail-header">
        <div className="app-rail-logo">
          <i className="f7-icons">leaf_fill</i>
        </div>
      </div>

      {/* Destinations - Operations, a divider, then Functionality. The rail is too
          narrow for text section headers, so the divider carries the grouping. */}
      <nav className="app-rail-nav">
        {operationsItems.map(renderItem)}
        {functionalityItems.length > 0 && <div className="app-rail-divider" />}
        {functionalityItems.map(renderItem)}
      </nav>

      {/* Footer: language + account, pinned to the bottom of the rail */}
      <div className="app-rail-footer">
        <div className="app-rail-lang" ref={langRef}>
          {langOpen && (
            <div className="app-rail-lang-menu" role="menu">
              {LOCALES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={l.value === locale}
                  className={`app-sidebar-lang-option${l.value === locale ? ' app-sidebar-lang-option-active' : ''}`}
                  onClick={() => selectLocale(l.value)}
                >
                  {(() => {
                    const Flag = FLAGS[l.country];
                    return <Flag className="app-sidebar-flag" title={l.native} />;
                  })()}
                  <span className="app-sidebar-lang-option-label">{l.native}</span>
                  {l.value === locale && <i className="f7-icons">checkmark_alt</i>}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className={`app-rail-tool${langOpen ? ' app-rail-tool-active' : ''}`}
            onClick={() => setLangOpen((o) => !o)}
            title={currentLang?.native}
            aria-haspopup="menu"
            aria-expanded={langOpen}
          >
            {currentLang &&
              (() => {
                const Flag = FLAGS[currentLang.country];
                return <Flag className="app-rail-flag" title={currentLang.native} />;
              })()}
            <span className="app-rail-tool-label">{locale.toUpperCase()}</span>
          </button>
        </div>

        <button
          type="button"
          className="app-rail-account"
          onClick={handleLogout}
          title={`${email ?? ''} - ${t.signOut}`}
        >
          <span className="app-rail-avatar">{email ? getInitials(email) : 'U'}</span>
        </button>
      </div>
    </div>
  );
}
